import { formatDateTime, formatElapsedMs } from "@/lib/ui/format";

export type IndexJobTimingInput = {
  status: string;
  fullIndex: boolean;
  wikiOnly: boolean;
  embedOnly: boolean;
  createdAt: Date | string;
  updatedAt: Date | string;
};

export type IndexStageTiming = {
  when: string | null;
  durationLabel: string | null;
  durationMs: number | null;
};

export type IndexStageTimings = {
  text: IndexStageTiming;
  vectors: IndexStageTiming;
  facts: IndexStageTiming;
};

const EMPTY: IndexStageTiming = {
  when: null,
  durationLabel: null,
  durationMs: null,
};

function toDate(value: Date | string | null | undefined): Date | null {
  if (!value) {
    return null;
  }
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function jobDurationMs(job: IndexJobTimingInput): number | null {
  const start = toDate(job.createdAt);
  const end = toDate(job.updatedAt);
  if (!start || !end) {
    return null;
  }
  const ms = end.getTime() - start.getTime();
  return ms >= 0 ? ms : null;
}

function pickDoneJob(
  jobs: IndexJobTimingInput[],
  match: (job: IndexJobTimingInput) => boolean,
): IndexJobTimingInput | null {
  let best: IndexJobTimingInput | null = null;
  let bestAt = 0;
  for (const job of jobs) {
    if (job.status !== "DONE" || !match(job)) {
      continue;
    }
    const at = toDate(job.updatedAt)?.getTime() ?? 0;
    if (!best || at >= bestAt) {
      best = job;
      bestAt = at;
    }
  }
  return best;
}

function fromDedicatedJob(
  job: IndexJobTimingInput,
  timeZone?: string,
): IndexStageTiming {
  const finished = toDate(job.updatedAt);
  const durationMs = jobDurationMs(job);
  return {
    when: finished ? formatDateTime(finished, timeZone) : null,
    durationMs,
    durationLabel: durationMs == null ? null : formatElapsedMs(durationMs),
  };
}

function whenOnly(
  value: Date | string | null | undefined,
  timeZone?: string,
): IndexStageTiming {
  const finished = toDate(value);
  return {
    when: finished ? formatDateTime(finished, timeZone) : null,
    durationMs: null,
    durationLabel: null,
  };
}

export function documentIndexStageTimings(input: {
  jobs: IndexJobTimingInput[];
  parseStatus: string;
  vectorIndexedAt: Date | string | null;
  extractStatus: string;
  timeZone?: string;
}): IndexStageTimings {
  const textOnly = pickDoneJob(
    input.jobs,
    (job) => !job.wikiOnly && !job.embedOnly && !job.fullIndex,
  );
  const fullJob = pickDoneJob(
    input.jobs,
    (job) => job.fullIndex && !job.wikiOnly && !job.embedOnly,
  );
  const embedJob = pickDoneJob(input.jobs, (job) => job.embedOnly);
  const wikiJob = pickDoneJob(input.jobs, (job) => job.wikiOnly);

  const text =
    input.parseStatus !== "DONE"
      ? EMPTY
      : textOnly
        ? fromDedicatedJob(textOnly, input.timeZone)
        : whenOnly(fullJob?.updatedAt ?? null, input.timeZone);

  const vectors = !input.vectorIndexedAt
    ? EMPTY
    : embedJob
      ? fromDedicatedJob(embedJob, input.timeZone)
      : whenOnly(input.vectorIndexedAt, input.timeZone);

  const facts =
    input.extractStatus !== "DONE"
      ? EMPTY
      : wikiJob
        ? fromDedicatedJob(wikiJob, input.timeZone)
        : whenOnly(fullJob?.updatedAt ?? null, input.timeZone);

  return { text, vectors, facts };
}

export function liveStageElapsedMs(input: {
  stageId: "text" | "ocr" | "vectors" | "facts";
  indexStage: string | null;
  indexStageAt: Date | string | null;
  now?: Date;
}): number | null {
  const map: Record<string, "text" | "vectors" | "facts"> = {
    PARSE: "text",
    EMBED: "vectors",
    EXTRACT: "facts",
  };
  const active = input.indexStage ? map[input.indexStage] : null;
  if (!active) {
    return null;
  }
  const matches =
    active === input.stageId ||
    (active === "text" && input.stageId === "ocr");
  if (!matches) {
    return null;
  }
  const started = toDate(input.indexStageAt);
  if (!started) {
    return null;
  }
  return Math.max(0, (input.now ?? new Date()).getTime() - started.getTime());
}
